import React, {useState} from 'react';
import graphql from 'babel-plugin-relay/macro';
import {useIntl} from 'react-intl';
import {ForecastQueryRenderer} from '../../../ForecastQueryRenderer';
import EditClientContactModal from './EditClientContactModal';
import ClientContactModalProjectsLoader from './ClientContactModalProjectsLoader';
import Util from '../../shared/util/util';
import UpdateClientContactMutation from '../../../mutations/UpdateClientContactMutation';
import {createToast} from '../../shared/components/toasts/another-toast/toaster';
import type {ClientContactFormValues} from './hooks/useClientContactForm';
import type {EditClientContactModalContainerQuery$data} from './__generated__/EditClientContactModalContainerQuery.graphql';

export const editClientContactModalContainerQuery = graphql`
	query EditClientContactModalContainerQuery($id: ID!) {
		node(id: $id) {
			... on ClientContact {
				id
				firstName
				lastName
				email
				jobTitle
				phone
				isGuestUser
				status
				client {
					id
					name
				}
				projects {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	}
`;

type ContactNode = NonNullable<EditClientContactModalContainerQuery$data['node']>;

type EditClientContactFormProps = {
	contact: ContactNode;
	onClose: () => void;
};

const EditClientContactForm = ({contact, onClose}: EditClientContactFormProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [isGuestUser, setIsGuestUser] = useState(contact.isGuestUser ?? false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleGuestUserToggle = () => setIsGuestUser(prev => !prev);

	const initialValues: ClientContactFormValues = {
		firstName: contact.firstName ?? '',
		lastName: contact.lastName ?? '',
		email: contact.email ?? '',
		jobTitle: contact.jobTitle ?? '',
		phone: contact.phone ?? '',
		projectIds: (contact.projects?.edges ?? []).map(e => e?.node?.id).filter((id): id is string => !!id),
		status: contact.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
	};

	const handleSubmit = (values: ClientContactFormValues) => {
		if (isSubmitting) return;

		setIsSubmitting(true);

		const statusChanged = values.status !== initialValues.status;

		const input = {
			id: contact.id,
			firstName: values.firstName,
			lastName: values.lastName,
			email: values.email,
			jobTitle: values.jobTitle || undefined,
			phone: values.phone || undefined,
			isGuestUser,
			...(isGuestUser ? {projectIds: values.projectIds} : {}),
			...(statusChanged ? {status: values.status} : {}),
		};

		const onSuccess = () => {
			setIsSubmitting(false);
			createToast({
				duration: 5000,
				message: formatMessage({id: 'client_contact.snackbar.contact_updated'}),
			});
			onClose();
		};

		const onError = () => {
			setIsSubmitting(false);
			createToast({
				duration: 5000,
				message: formatMessage({id: 'client_contact.snackbar.contact_update_failed'}),
			});
		};

		Util.CommitMutation(UpdateClientContactMutation, input, onSuccess, true, null, onError);
	};

	return (
		<ClientContactModalProjectsLoader clientId={contact.client?.id ?? ''}>
			{({projectOptions, isProjectsLoading}) => (
				<EditClientContactModal
					clientName={contact.client?.name ?? ''}
					projectOptions={projectOptions}
					isProjectsLoading={isProjectsLoading}
					initialValues={initialValues}
					isGuestUser={isGuestUser}
					onGuestUserToggle={handleGuestUserToggle}
					onClose={onClose}
					onSubmit={handleSubmit}
					isSubmitting={isSubmitting}
				/>
			)}
		</ClientContactModalProjectsLoader>
	);
};

export type EditClientContactModalContainerProps = {
	clientContactId: string;
	onClose: () => void;
};

const EditClientContactModalContainer = ({clientContactId, onClose}: EditClientContactModalContainerProps): JSX.Element => (
	<ForecastQueryRenderer
		key={`edit-client-contact-${clientContactId}`}
		query={editClientContactModalContainerQuery}
		modal={true}
		alwaysRender={true}
		renderIfNotLoggedIn={true}
		variables={{id: clientContactId}}
		render={(relayProps: EditClientContactModalContainerQuery$data | null) => {
			if (!relayProps?.node) {
				return null;
			}
			return <EditClientContactForm contact={relayProps.node} onClose={onClose} />;
		}}
	/>
);

export default EditClientContactModalContainer;
